import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Checkbox,
  Grid,
  FormControlLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import PatientDatas from "src/modules/medcard/patient-datas";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { ByzColors } from "src/theme/colors";
import { useTranslate } from "src/locales";
import { RootState } from "src/app/store";
import { requestService as reqSvc, OperationSeanceDto, requestService } from "src/api/request/requestService";
import { PatientComingFileChangeModal } from "src/components/Add-Service-Modals";

export interface EditOperationModalProps {
  open: boolean;
  onClose: () => void;
  operationData: {
    id: number;
    name: string;
    quantity: number;
    direction?: string;
    session?: string;
    doctor?: string;
    doctorRole?: string;
    changeOption?: string;
    generalSession?: string;
    generalSessionId?: string;
    serviceType?: string;
  };
  patientId: string;
  onSave: (updatedData: { id: number; quantity: number; direction?: string; session?: string; doctor?: string; doctorRole?: string; changeOption?: string; generalSession?: string; generalSessionId?: string }) => void;
  onDoctorInfoSave?: (data: {
    PatientTypeId: number | null;
    DeptId: number | null;
    DoctorId: number | null;
    DoctorRoleId?: number | null;
    RefDoctorId?: number | null;
    AssistantDoctorId?: number | null;
    RefPersonelId?: number | null;
  }) => void;
}

const EditOperationModal: React.FC<EditOperationModalProps> = ({
  open,
  onClose,
  operationData,
  patientId,
  onSave,
  onDoctorInfoSave,
}) => {
  const { t } = useTranslate();
  const selectedOfficeId = useSelector((state: RootState) => state.auth.selectedOfficeId);
  
  const directionLabels = React.useMemo(() => ({
    1: t("patient-request-service.direction-options.right"),
    2: t("patient-request-service.direction-options.left"),
    3: t("patient-request-service.direction-options.none"),
  }), [t]);

  // Yön text'ini value'ya çevir
  const getDirectionValue = React.useCallback((text: string): string => {
    if (!text) return "";
    const normalized = text.trim();
    const entry = Object.entries(directionLabels).find(([, label]) => label === normalized);
    if (entry) return entry[0];
    if (normalized === "Sağ" || normalized === "Right") return "1";
    if (normalized === "Sol" || normalized === "Left") return "2";
    if (normalized === "Ayrım Yok" || normalized === "No Difference") return "3";
    if (normalized === "1" || normalized === "2" || normalized === "3") return normalized;
    return normalized;
  }, [directionLabels]);

  // Yön value'yu text'e çevir
  const getDirectionText = React.useCallback((value: string): string => {
    if (!value) return "";
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && directionLabels[numeric as 1 | 2 | 3]) {
      return directionLabels[numeric as 1 | 2 | 3];
    }
    if (value === "Sağ" || value === "Right") return directionLabels[1];
    if (value === "Sol" || value === "Left") return directionLabels[2];
    if (value === "Ayrım Yok" || value === "No Difference") return directionLabels[3];
    return value;
  }, [directionLabels]);

  const [quantity, setQuantity] = useState(operationData.quantity.toString());
  const [direction, setDirection] = useState(getDirectionValue(operationData.direction || ""));
  const [session, setSession] = useState(operationData.session || "");
  const [doctor, setDoctor] = useState(operationData.doctor || "");
  const [doctorRole, setDoctorRole] = useState(operationData.doctorRole || "");
  const [changeOption, setChangeOption] = useState(operationData.changeOption || "");
  const [generalSession, setGeneralSession] = useState(operationData.generalSession || "");
  const [generalSessionId, setGeneralSessionId] = useState<number | null>(
    operationData.generalSessionId ? parseInt(operationData.generalSessionId) : null
  );
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [operationSeances, setOperationSeances] = useState<OperationSeanceDto[]>([]);
  const [loadingSeances, setLoadingSeances] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    operationData.generalSessionId ? parseInt(operationData.generalSessionId) : null
  );
  // API'den gelecek checkbox değerleri - şimdilik false, sonra API'den doldurulacak
  const [patientPayWillChange, setPatientPayWillChange] = useState(false);
  const [patientServiceDifference, setPatientServiceDifference] = useState(false);
  const [additionalInstitutionPay, setAdditionalInstitutionPay] = useState(false);
  const [openDoctorSelectionModal, setOpenDoctorSelectionModal] = useState(false);

  const isSessionEditable = React.useMemo(() => {
    const normalized = (operationData.serviceType || "").trim().toLocaleLowerCase("tr-TR");
    return (
      normalized === "" ||
      normalized === "ameliyat" ||
      normalized === "diş" ||
      normalized === "radyoloji"
    );
  }, [operationData.serviceType]);

  useEffect(() => {
    const fetchSeances = async () => {
      try {
        setLoadingSeances(true);
        const res = await reqSvc.getOperationSeances();
        if (res && res.data && Array.isArray(res.data)) {
          setOperationSeances(res.data);
        }
      } catch (error) {
        console.error("Error loading operation seances:", error);
      } finally {
        setLoadingSeances(false);
      }
    };
    if (sessionModalOpen && operationSeances.length === 0) {
      fetchSeances();
    }
    // Seans modalı açıldığında mevcut seçimi göster (eğer varsa)
    if (sessionModalOpen) {
      setSelectedSessionId(generalSessionId);
    }
  }, [sessionModalOpen, operationSeances.length, generalSessionId]);

  // Track the last edited operation ID to prevent resetting on same operation
  const lastOperationIdRef = React.useRef<number | null>(null);

  // Update states when operationData changes or modal opens
  useEffect(() => {
    if (!open) {
      // Reset ref when modal closes
      lastOperationIdRef.current = null;
      return;
    }
    
    // Only reset if this is a different operation or modal just opened
    const currentOperationId = operationData.id;
    if (lastOperationIdRef.current === currentOperationId) {
      // Same operation, don't reset - user might be editing
      return;
    }
    
    // New operation or modal just opened, update states
    lastOperationIdRef.current = currentOperationId;
    setQuantity(operationData.quantity.toString());
    setDirection(getDirectionValue(operationData.direction || "")); // Text'i value'ya çevir
    setSession(operationData.session || "");
    setDoctor(operationData.doctor || "");
    setDoctorRole(operationData.doctorRole || "");
    setChangeOption(operationData.changeOption || "");
    setGeneralSession(operationData.generalSession || "");
    
    // Seans ID'sini sadece varsa set et, yoksa null yap
    const sessionId = operationData.generalSessionId ? parseInt(operationData.generalSessionId) : null;
    setGeneralSessionId(sessionId);
    setSelectedSessionId(sessionId); // Her modal açıldığında seçili seansı güncelle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationData.id, open, getDirectionValue]);

  useEffect(() => {
    if (!isSessionEditable && sessionModalOpen) {
      setSessionModalOpen(false);
    }
  }, [isSessionEditable, sessionModalOpen]);

  // Fetch Process Control Price when modal opens
  useEffect(() => {
    const fetchProcessControlPrice = async () => {
      if (!open || !selectedOfficeId) return;
      
      try {
        const response = await requestService.getProcessControlPrice({
          code: 2,
          offID: selectedOfficeId,
        });
        
        // API response'una göre checkbox değerlerini set et
        if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
          const priceData = response.data[0];
          setPatientPayWillChange(priceData.change_pat_price === "1");
          setPatientServiceDifference(priceData.change_pat_diff_price === "1");
          setAdditionalInstitutionPay(priceData.change_sec_ass_price === "1");
        }
      } catch (error) {
        console.error("Error fetching process control price:", error);
      }
    };

    fetchProcessControlPrice();
  }, [open, selectedOfficeId]);

  const handleSave = () => {
    const updatedData = {
      id: operationData.id,
      quantity: parseInt(quantity) || 1,
      direction: getDirectionText(direction), // Value'yu text'e çevir
      session: generalSession || session, // Seans alanını generalSession ile güncelle
      doctor,
      doctorRole,
      changeOption,
      generalSession,
      generalSessionId: generalSessionId?.toString() || "",
    };
    onSave(updatedData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "60vh",
          maxHeight: "70vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          p: 1,
          px: 3,
          mt: 5,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("patient-request-service.edit-operation-modal.title")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pb: 1 }}>
        <Box sx={{ p: 2 }}>
          <PatientDatas
            patientId={patientId}
            isOpenMedCard={true}
            isCreateMedCard={true}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid #000000",
            mx: "2%",
          }}
        >
          <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
            <ByzMuiSaveButton onClick={handleSave} />
          </Box>
          <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
            <ByzMuiCloseButton onClick={onClose} />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", mb: 2 }}>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
                width: "50%",
                pl: 1,
                backgroundColor: "#F4F7FE",
                px: 1,
                py: 1.5,
              }}
            >
              {t("patient-request-service.edit-operation-modal.change")}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                }}
              >
                {t("patient-request-service.edit-operation-modal.amount")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <ByzMuiTextField
                label={t("patient-request-service.edit-operation-modal.amount")}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                fullWidth={false}
                InputLabelProps={{ shrink: true, style: { color: ByzColors.ByzBlue } }}
                inputProps={{ style: { cursor: "pointer", width: "100%" } }}
                sx={{
                  width: "50%",
                  cursor: "pointer",
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                }}
              >
                {t("patient-request-service.edit-operation-modal.doctor")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ width: "50%" }}>
                <ByzMuiTextField
                  label={t("patient-request-service.edit-operation-modal.doctor")}
                  value={doctor}
                  onClick={() => setOpenDoctorSelectionModal(true)}
                  fullWidth
                  InputLabelProps={{ shrink: true, style: { color: ByzColors.ByzBlue } }}
                  inputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    placeholder: "",
                  }}
                  sx={{
                    cursor: "pointer",
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                }}
              >
                {t("patient-request-service.edit-operation-modal.doctor-role")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ width: "50%" }}>
                <ByzMuiTextField
                  label={t("patient-request-service.edit-operation-modal.doctor-role")}
                  value={doctorRole}
                  onClick={() => setOpenDoctorSelectionModal(true)}
                  fullWidth
                  InputLabelProps={{ shrink: true, style: { color: ByzColors.ByzBlue } }}
                  inputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    placeholder: "",
                  }}
                  sx={{
                    cursor: "pointer",
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                }}
              >
                {t("patient-request-service.edit-operation-modal.direction")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <ByzMuiFormSelect
                label={t("patient-request-service.edit-operation-modal.direction")}
                value={direction}
                onChange={(e) => setDirection(String(e.target.value))}
                fullWidth={false}
                FormControlSx={{ width: "50%" }}
              >
                <MenuItem value="">{t("patient-request-service.edit-operation-modal.select")}</MenuItem>
                <MenuItem value="1">{t("patient-request-service.direction-options.right")}</MenuItem>
                <MenuItem value="2">{t("patient-request-service.direction-options.left")}</MenuItem>
                <MenuItem value="3">{t("patient-request-service.direction-options.none")}</MenuItem>
              </ByzMuiFormSelect>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px",
                }}
              >
                {t("patient-request-service.edit-operation-modal.session")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ width: "50%" }}>
                <ByzMuiTextField
                  label={t("patient-request-service.edit-operation-modal.session")}
                  value={generalSession || ""}
                  onClick={isSessionEditable ? () => setSessionModalOpen(true) : undefined}
                  disabled={!isSessionEditable}
                  fullWidth
                  InputLabelProps={{ shrink: true, style: { color: ByzColors.ByzBlue } }}
                  inputProps={{
                    readOnly: true,
                    style: { cursor: isSessionEditable ? "pointer" : "not-allowed" },
                    placeholder: "",
                  }}
                  sx={{
                    cursor: isSessionEditable ? "pointer" : "not-allowed",
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2498f3" },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        {/* Session selection modal - same shell as EditOperationModal */}
        <Dialog
          open={sessionModalOpen}
          onClose={() => setSessionModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              minHeight: "60vh",
              maxHeight: "70vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              p: 1,
              px: 3,
              mt: 5,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {t("patient-request-service.edit-operation-modal.session-modal.title")}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: 0, pb: 1 }}>
            <Box sx={{ p: 2 }}>
              <PatientDatas patientId={patientId} isOpenMedCard={true} isCreateMedCard={true} />
            </Box>

            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #000000",
                mx: "2%",
              }}
            >
              <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
                <ByzMuiSaveButton onClick={() => {
                  if (selectedSessionId !== null) {
                    const selectedSession = operationSeances.find(s => s.id === selectedSessionId);
                    if (selectedSession) {
                      setGeneralSession(selectedSession.name);
                      setGeneralSessionId(selectedSession.id);
                    }
                  }
                  setSessionModalOpen(false);
                }} />
              </Box>
              <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
                <ByzMuiCloseButton onClick={() => {
                  setSelectedSessionId(null);
                  setSessionModalOpen(false);
                }} />
              </Box>
            </Box>

            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", mb: 2 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    borderBottom: "1px solid #e0e0e0",
                    pb: 1,
                    width: "50%",
                    pl: 1,
                    backgroundColor: "#F4F7FE",
                    px: 1,
                    py: 1.5,
                  }}
                >
                  {t("patient-request-service.edit-operation-modal.session-modal.selection")}
                </Typography>
              </Box>

              <Box sx={{ border: "1px solid #2daeff", borderRadius: 1, overflow: "hidden" }}>
                <Box sx={{ display: "flex", bgcolor: "#f7fbff", borderBottom: "1px solid #2daeff" }}>
                  <Box sx={{ width: 80, p: 1, fontWeight: 600 }}>
                    {t("patient-request-service.edit-operation-modal.session-modal.columns.id")}
                  </Box>
                  <Box sx={{ flex: 1, p: 1, fontWeight: 600 }}>
                    {t("patient-request-service.edit-operation-modal.session-modal.columns.name")}
                  </Box>
                  <Box sx={{ width: 140, p: 1, fontWeight: 600, textAlign: "right", pr: 2 }}>
                    {t("patient-request-service.edit-operation-modal.session-modal.columns.coefficient")}
                  </Box>
                </Box>
                {(loadingSeances ? [] : operationSeances).map((row) => (
                  <Box key={row.id} sx={{ display: "flex", alignItems: "center", borderBottom: "1px dashed #bde2ff" }}>
                    <Box sx={{ width: 80, p: 1 }}>
                      <Checkbox
                        checked={selectedSessionId === row.id}
                        onChange={() => setSelectedSessionId(row.id)}
                        size="small"
                      />
                      {row.id}
                    </Box>
                    <Box sx={{ flex: 1, p: 1 }}>{row.name}</Box>
                    <Box sx={{ width: 140, p: 1, textAlign: "right", pr: 2 }}>{row.quantity}</Box>
                  </Box>
                ))}
                {loadingSeances && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("patient-request-service.edit-operation-modal.session-modal.loading")}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("patient-request-service.edit-operation-modal.session-modal.note")}
                </Typography>
                <Box sx={{ mt: 2, ml:2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {t("patient-request-service.edit-operation-modal.session-modal.changes.title")}
                  </Typography>
                  <Box>
                    <FormControlLabel 
                      control={
                        <ByzMuiCheckBox 
                          checked={patientPayWillChange}
                          onClick={(e: any) => e.preventDefault()}
                          sx={{ pointerEvents: "none" }}
                        />
                      } 
                      label={t("patient-request-service.edit-operation-modal.session-modal.changes.patient-share")} 
                    />
                  </Box>
                  <Box>
                    <FormControlLabel 
                      control={
                        <ByzMuiCheckBox 
                          checked={patientServiceDifference}
                          onClick={(e: any) => e.preventDefault()}
                          sx={{ pointerEvents: "none" }}
                        />
                      } 
                      label={t("patient-request-service.edit-operation-modal.session-modal.changes.service-difference")} 
                    />
                  </Box>
                  <Box>
                    <FormControlLabel 
                      control={
                        <ByzMuiCheckBox 
                          checked={additionalInstitutionPay}
                          onClick={(e: any) => e.preventDefault()}
                          sx={{ pointerEvents: "none" }}
                        />
                      } 
                      label={t("patient-request-service.edit-operation-modal.session-modal.changes.institution-share")} 
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        <PatientComingFileChangeModal
          open={openDoctorSelectionModal}
          onClose={() => setOpenDoctorSelectionModal(false)}
          editOperationMode={true}
          serviceName={operationData.name}
          onDoctorSelected={(data) => {
            setDoctor(data.doctorName);
            setDoctorRole(data.doctorRoleName);
            onDoctorInfoSave?.({
              PatientTypeId: data.arrivalTypeId ? Number(data.arrivalTypeId) : null,
              DeptId: data.departmentId ? Number(data.departmentId) : null,
              DoctorId: data.doctorId ? Number(data.doctorId) : null,
              DoctorRoleId: data.doctorRoleId ? Number(data.doctorRoleId) : null,
            });
            setOpenDoctorSelectionModal(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditOperationModal;
